import type { IconName } from "@mdotm/shadcn/legacy/components";
import { dateYYYYMMDD } from "$root/utils";
import type { PropagationState } from "./state";

export type PropagationMessageKind = "error" | "running-dirty" | "running" | "scheduled" | "done";

export type PropagationMessage = {
	kind: PropagationMessageKind;
	label: string;
	icon: IconName;
};

const iconByKind = {
	error: "Alert",
	"running-dirty": "calulating",
	running: "calulating",
	scheduled: "Clock",
	done: "Outline",
} satisfies Record<PropagationMessageKind, IconName>;

/** The two `useLocaleFormatters` stamps row 5 chooses between. */
export type PropagationTimeFormatters = {
	formatTime: (date: Date) => string;
	formatDateTime: (date: Date) => string;
};

/** Both moments fall on the same local calendar day, in the browser's timezone. */
function isSameLocalDay(a: Date, b: Date): boolean {
	return dateYYYYMMDD(a) === dateYYYYMMDD(b);
}

/**
 * The propagation half of the indicator row is a total function of `(status, dirty, lastRun)`,
 * evaluated in precedence order — several conditions can hold at once.
 *
 * | # | Condition                                              | Copy                                                          |
 * | - | ------------------------------------------------------ | ------------------------------------------------------------- |
 * | 1 | `lastRun.outcome === "ERROR"`, nothing newer supersedes | `Could not apply to portfolios`                               |
 * | 2 | `status === "RUNNING"` and `dirty`                      | `Applying to portfolios… · newer changes queued for the next run` |
 * | 3 | `status === "RUNNING"`                                  | `Applying to portfolios…`                                     |
 * | 4 | `status === "SCHEDULED"`                                | `Changes will be applied to portfolios at 14:47`              |
 * | 5 | `lastRun.outcome === "SUCCESS"`                          | `Applied to portfolios at 14:49`                              |
 * | 6 | otherwise                                              | nothing shown                                                 |
 *
 * Row 1's "nothing newer supersedes it" is read as `status === "IDLE"`: a run in progress or a run
 * already scheduled *is* newer information, and a scheduled run also auto-heals the failure, so
 * announcing a past failure over it would hide the work that is actually pending.
 *
 * Row 5 has no expiry — it mirrors the `Saved at …` half, which also persists until the next run.
 * It carries a bare finish time only while the run is from `now`'s local calendar day, and the full
 * `formatDateTime` stamp (`Applied to portfolios on 4 Aug 2026 2:49 PM`) once it is not: on a page
 * left open overnight, `at 14:49` reads as a run that has just happened. The day comes from the
 * `now` argument, so the function stays pure (no timers) — a render is what re-reads the clock.
 *
 * Past tense is correct only in row 5: the scheduled state must read as future, or it claims work
 * that has not happened yet.
 */
export function propagationMessage(
	state: PropagationState | null,
	{ formatTime, formatDateTime }: PropagationTimeFormatters,
	now: Date,
): PropagationMessage | null {
	if (state === null) {
		return null;
	}

	if (state.lastRun?.outcome === "ERROR" && state.status === "IDLE") {
		return { kind: "error", label: "Could not apply to portfolios", icon: iconByKind.error };
	}

	if (state.status === "RUNNING") {
		return state.dirty
			? {
					kind: "running-dirty",
					label: "Applying to portfolios… · newer changes queued for the next run",
					icon: iconByKind["running-dirty"],
				}
			: { kind: "running", label: "Applying to portfolios…", icon: iconByKind.running };
	}

	if (state.status === "SCHEDULED") {
		return {
			kind: "scheduled",
			label:
				state.scheduledAt == null
					? "Changes will be applied to portfolios"
					: `Changes will be applied to portfolios at ${formatTime(new Date(state.scheduledAt))}`,
			icon: iconByKind.scheduled,
		};
	}

	if (state.lastRun?.outcome === "SUCCESS") {
		const finishedAt = new Date(state.lastRun.finishedAt);
		return {
			kind: "done",
			label: isSameLocalDay(finishedAt, now)
				? `Applied to portfolios at ${formatTime(finishedAt)}`
				: `Applied to portfolios on ${formatDateTime(finishedAt)}`,
			icon: iconByKind.done,
		};
	}

	return null;
}

export type PropagateNowGate = {
	/** `null` while the state `GET` has not answered yet. */
	propagation: PropagationState | null;
	/** `TaskQueueState.isRunning` of the cell-edit save queue. */
	isSaveQueueRunning: boolean;
	/**
	 * Commentary generations whose asynchronous write has not landed yet. The bulk path does not go
	 * through the save queue and its writes complete well after the HTTP call resolves, so the
	 * queue-busy gate gives no ordering guarantee here.
	 */
	outstandingCommentaryGenerations: number;
	/** Cosmetic mirror of the server-side authorization check on the flush endpoint. */
	canPropagate: boolean;
};

export type PropagateNowState = "hidden" | "disabled" | "enabled";

/**
 * The `Apply now` action lives next to the propagation message, so unavailability is expressed
 * by hiding it rather than by parking a dead control in the middle of a sentence. Evaluated in
 * precedence order:
 *
 * | # | Condition                                                         | Result     |
 * | - | ----------------------------------------------------------------- | ---------- |
 * | 1 | no permission / state not read yet / a run is already in progress | `hidden`   |
 * | 2 | nothing pending — a flush would have nothing to do                | `hidden`   |
 * | 3 | a save or a commentary generation has not landed yet              | `disabled` |
 * | 4 | otherwise                                                         | `enabled`  |
 *
 * Rows 1–2 are properties of the *server's* propagation state: they change only when a run is
 * scheduled, starts, or finishes, so hiding on them cannot flicker. Row 3 is the opposite —
 * `isSaveQueueRunning` flips true on every cell commit — which is why it only ever disables. Once
 * the row is visible it stays visible for the whole edit-then-flush cycle.
 *
 * Deliberately *not* a condition: a failed save. A failed `PATCH` never reached the server, so
 * nothing is dirty from that edit and a flush propagates the server's true state — exactly what the
 * scheduled run would have propagated anyway.
 */
export function propagateNowState({
	propagation,
	isSaveQueueRunning,
	outstandingCommentaryGenerations,
	canPropagate,
}: PropagateNowGate): PropagateNowState {
	if (!isPropagateNowVisible({ propagation, canPropagate })) {
		return "hidden";
	}
	if (isSaveQueueRunning || outstandingCommentaryGenerations > 0) {
		return "disabled";
	}
	return "enabled";
}

/**
 * Rows 1–2 of {@link propagateNowState}, on their own: the caller mounts the action on this alone,
 * so that the parts of the gate that *do* change while the user types can only ever disable it.
 */
export function isPropagateNowVisible({
	propagation,
	canPropagate,
}: Pick<PropagateNowGate, "propagation" | "canPropagate">): boolean {
	if (!canPropagate || propagation === null || propagation.status === "RUNNING") {
		return false;
	}
	// Nothing pending: a flush would have nothing to do.
	return propagation.dirty || propagation.scheduledAt != null;
}
